import React, { useEffect, useRef, useState } from "react";
import { Box, FormHelperText, MenuItem, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DurationUnit, getAttributeUnit, setAttributeUnit } from "../../utils/attributeUnitStorage";

/**
 * Sentinel inputType used to route a TDA number attribute to this renderer.
 *
 * The shared `TDAForm` keys `customInputRenderers` by `attribute.inputType` and resolves
 * built-in renderers (incl. `"number"`) *before* consulting them. So a stock `"number"`
 * attribute can never reach a custom renderer — we re-tag the attribute with this unique
 * inputType (see {@link withNumberWithUnitInputType}) so it falls through to the
 * custom-renderer slot instead.
 */
export const NUMBER_WITH_UNIT_INPUT_TYPE = "numberWithUnit";

/**
 * Attribute indices that should always render with the number/unit (Days/Hours) input.
 * The backend stores these in hours; the unit toggle is frontend-only. Applied centrally
 * in {@link InstalledBaseTDAForm} so every TDA form picks them up automatically.
 */
export const NUMBER_WITH_UNIT_ATTRIBUTE_INDICES = ["runningTimeHours", "pumpAgeAtInstallHours"];

/**
 * Returns a shallow copy of `attributes` with the given indices re-tagged to use the
 * {@link NUMBER_WITH_UNIT_INPUT_TYPE} renderer. Does not mutate the input.
 *
 * `type` is intentionally left untouched (e.g. `"int"`) so the generated numeric Yup
 * schema still validates the stored hours value directly.
 */
export const withNumberWithUnitInputType = (attributes: any[], indices: string[]): any[] =>
    (attributes ?? []).map((attr) =>
        indices.includes(attr.index) ? { ...attr, inputType: NUMBER_WITH_UNIT_INPUT_TYPE } : attr
    );

const HOURS_PER_DAY = 24;

/** Formats a stored hours value into the input's display text for the given unit. */
const formatForUnit = (hours: number | null | undefined, unit: DurationUnit): string => {
    if (hours === null || hours === undefined || Number.isNaN(hours)) return "";
    if (unit === "days") {
        // Allow fractional days, rounded to 2 decimals for display.
        return String(Math.round((hours / HOURS_PER_DAY) * 100) / 100);
    }
    return String(hours);
};

/** Parses the input's display text into a stored hours value (integer) for the given unit. */
const parseToHours = (text: string, unit: DurationUnit): number | null => {
    const trimmed = text.trim().replace(",", ".");
    if (trimmed === "") return null;
    const n = Number(trimmed);
    if (Number.isNaN(n)) return null;
    return unit === "days" ? Math.round(n * HOURS_PER_DAY) : Math.round(n);
};

/**
 * Props injected by `TDAForm`'s `customInputRenderers` slot.
 * @see InstalledBaseTDAForm
 */
interface Props {
    attribute: any;
    value?: number | null;
    error?: string;
    touched?: boolean;
    setFieldValue: (index: string, value: unknown) => void;
}

/**
 * Number input paired with a small Days/Hours unit selector on one line. The stored
 * form value is always in hours; Days↔Hours conversion happens only in the frontend.
 * The last-selected unit is remembered per attribute index in localStorage.
 */
const NumberWithUnitInput = ({ attribute, value, error, touched, setFieldValue }: Props) => {
    const { t } = useTranslation();

    const isRequired = Boolean(attribute?.rules?.find((rule: any) => rule.name === "required"));
    const label = `${attribute?.name ?? ""}${isRequired ? " *" : ""}`;
    const showError = Boolean(error && touched);

    const [unit, setUnit] = useState<DurationUnit>(() => getAttributeUnit(attribute.index) ?? "days");
    const [text, setText] = useState<string>(() => formatForUnit(value, getAttributeUnit(attribute.index) ?? "days"));

    // Tracks the hours value we last wrote, so an external value change (prefill,
    // reset) re-syncs the input text while our own keystroke-driven writes don't
    // reformat what the user is mid-typing (e.g. "2." would collapse to "2").
    const lastWrittenRef = useRef<number | null | undefined>(value);

    useEffect(() => {
        if (value !== lastWrittenRef.current) {
            lastWrittenRef.current = value;
            setText(formatForUnit(value, unit));
        }
    }, [value, unit]);

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const nextText = e.target.value;
        setText(nextText);
        const hours = parseToHours(nextText, unit);
        lastWrittenRef.current = hours;
        setFieldValue(attribute.index, hours);
    };

    const handleUnitChange = (nextUnit: DurationUnit | null) => {
        if (!nextUnit || nextUnit === unit) return;
        setAttributeUnit(attribute.index, nextUnit);
        setUnit(nextUnit);
        // The stored hours value is unchanged; only its display representation differs.
        lastWrittenRef.current = value;
        setText(formatForUnit(value, nextUnit));
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", width: "100%" }}>
                <TextField
                    id={attribute.index}
                    name={attribute.index}
                    label={label}
                    type="number"
                    size="small"
                    fullWidth
                    disabled={attribute.disabled}
                    value={text}
                    onChange={handleTextChange}
                    InputLabelProps={{ shrink: true }}
                    error={showError}
                    sx={{ flex: 1 }}
                />
                <TextField
                    select
                    size="small"
                    value={unit}
                    onChange={(e) => handleUnitChange(e.target.value as DurationUnit)}
                    disabled={attribute.disabled}
                    sx={{ width: 120, flexShrink: 0 }}
                >
                    <MenuItem value="days">{t("Days")}</MenuItem>
                    <MenuItem value="hours">{t("Hours")}</MenuItem>
                </TextField>
            </Box>
            {showError ? (
                <FormHelperText error>{error}</FormHelperText>
            ) : (
                attribute?.hint && <FormHelperText>{attribute.hint}</FormHelperText>
            )}
        </Box>
    );
};

export default NumberWithUnitInput;
